import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Edit, Shield, ShieldOff, Copy, Eye, Trash2, Mail, Link } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { z } from "zod";
import { findUser } from "@shared/validators/user-validator";

type User = z.infer<typeof findUser>;

interface UserActionsProps {
    user: User;
    onEdit?: (user: User) => void;
    onViewDetails?: (user: User) => void;
    onToggleEnabled?: (user: User) => void;
    onDuplicate?: (user: User) => void;
    onDelete?: (user: User) => void;
    onResendInvite?: (user: User) => void;
    onCopyInviteLink?: (user: User) => void;
}

export function UserActions({
    user,
    onEdit,
    onViewDetails,
    onToggleEnabled,
    onDuplicate,
    onDelete,
    onResendInvite,
    onCopyInviteLink
}: UserActionsProps) {
    const handleToggleEnabled = () => {
        if (!onToggleEnabled) return;
        onToggleEnabled(user);
    };

    const handleDelete = () => {
        if (!onDelete) return;
        onDelete(user);
    };

    const isInvited = user.authSource === "invited";

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button aria-label="Open menu" variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
                    <DotsHorizontalIcon className="h-4 w-4" aria-hidden="true" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuItem onClick={() => onViewDetails?.(user)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit?.(user)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDuplicate?.(user)}>
                    <Copy className="mr-2 h-4 w-4" />
                    Duplicate
                </DropdownMenuItem>

                {isInvited && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onResendInvite?.(user)}>
                            <Mail className="mr-2 h-4 w-4" />
                            Resend Invite
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onCopyInviteLink?.(user)}>
                            <Link className="mr-2 h-4 w-4" />
                            Copy Invite Link
                        </DropdownMenuItem>
                    </>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleToggleEnabled}>
                    {!user.banned ? (
                        <>
                            <ShieldOff className="mr-2 h-4 w-4" />
                            Disable
                        </>
                    ) : (
                        <>
                            <Shield className="mr-2 h-4 w-4" />
                            Enable
                        </>
                    )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}